// The document a Page runs as (docs/plans/poneva-everywhere-2026-10-16/PAGES.md "Running a
// Page"): the page's own html with its CDN addresses pointed at Poneva's copies, the content
// policy and a small script as the document's first markup (before anything the page wrote), which
// keep the page inside its frame. Pure: no
// DOM. Shared by the server's page route (src/mcp-access/everywhere/page-route.mjs) and the
// Poneva view (ui/workspace/page-frame/page-document.ts re-exports it).
import { WEBRTC_BOOTSTRAP } from '../apps/runtime/no-webrtc.mjs';

export const PAGE_NS = 'poneva-page';

/** The policy from the spec, for the origin that serves the vendored libraries. */
export function pagePolicy(origin) {
  const vendor = `${origin}/everywhere/vendor/`;
  return [
    `default-src 'none'`,
    `script-src 'unsafe-inline' 'unsafe-eval' ${vendor}`,
    `style-src 'unsafe-inline' ${vendor}`,
    `font-src ${vendor} data:`,
    `img-src data: blob:`,
    `media-src data: blob:`,
    `connect-src 'none'`,
    `frame-src 'none'`,
    `form-action 'none'`,
    `base-uri 'none'`,
  ].join('; ');
}

const escapeAttribute = (value) =>
  value.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;');
const escapePattern = (value) => value.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
const htmlEscaped = (value) => value.replaceAll('&', '&amp;');

/**
 * Every resource's address, as written in the page (also with its ampersands escaped as an
 * attribute holds them, and protocol-relative), replaced by Poneva's copy in one pass so that
 * an address that begins another is never cut in two.
 */
export function rewriteResources(html, resources, origin) {
  const targets = new Map();
  for (const { url, sha256 } of resources) {
    const copy = `${origin}/everywhere/vendor/${sha256}`;
    for (const form of [url, htmlEscaped(url)]) {
      targets.set(form, copy);
      targets.set(form.replace(/^https?:/u, ''), copy);
    }
  }
  if (targets.size === 0) return html;
  const pattern = new RegExp(
    [...targets.keys()]
      .sort((a, b) => b.length - a.length)
      .map(escapePattern)
      .join('|'),
    'gu',
  );
  return html.replace(pattern, (found) => targets.get(found) ?? found);
}

/**
 * Runs inside the frame. Links and window.open go to the host, never navigate the frame.
 * Storage: an opaque-origin frame throws on localStorage and sessionStorage, so both get an
 * in-memory stand-in (kept while the page is open). `window.storage` is Claude's artifact
 * storage; its stand-in is async and answers with claude.ai's shapes:
 *   get(key)      -> { key, value, shared: false } or null when unset
 *   set(key, v)   -> { key, value, shared: false }
 *   delete(key)   -> { key, deleted: true, shared: false }
 *   list(prefix?) -> { keys: string[], prefix?, shared: false }
 * The page's height goes to the host on load, resize and content change. WebRTC is removed
 * first, as in Chat's saved Apps; no content policy governs it, and a page that builds a
 * frame of its own can still reach it there (PAGES.md "What the policy cannot stop").
 */
export const PAGE_SCRIPT = `${WEBRTC_BOOTSTRAP}(function () {
  var NS = ${JSON.stringify(PAGE_NS)};
  var parentWindow = window.parent;
  function send(message) {
    message.ns = NS;
    try { parentWindow.postMessage(message, '*'); } catch (e) {}
  }
  function web(url) {
    try {
      var parsed = new URL(String(url));
      return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
    } catch (e) { return null; }
  }
  document.addEventListener('click', function (event) {
    var node = event.target;
    var link = node && node.closest ? node.closest('a[href]') : null;
    if (!link) return;
    var raw = link.getAttribute('href') || '';
    if (raw.charAt(0) === '#') return;
    event.preventDefault();
    var url = web(raw);
    if (url) send({ type: 'open', url: url });
  }, true);
  window.open = function (url) {
    var target = web(url);
    if (target) send({ type: 'open', url: target });
    return null;
  };
  function memory() {
    var data = new Map();
    return {
      get length() { return data.size; },
      key: function (i) { return Array.from(data.keys())[i] || null; },
      getItem: function (k) { k = String(k); return data.has(k) ? data.get(k) : null; },
      setItem: function (k, v) { data.set(String(k), String(v)); },
      removeItem: function (k) { data.delete(String(k)); },
      clear: function () { data.clear(); }
    };
  }
  function define(name, value) {
    try {
      Object.defineProperty(window, name, { value: value, configurable: true, writable: true });
    } catch (e) {}
  }
  define('localStorage', memory());
  define('sessionStorage', memory());
  var kept = new Map();
  define('storage', {
    get: function (k) {
      k = String(k);
      return Promise.resolve(kept.has(k) ? { key: k, value: kept.get(k), shared: false } : null);
    },
    set: function (k, v) {
      k = String(k); v = String(v); kept.set(k, v);
      return Promise.resolve({ key: k, value: v, shared: false });
    },
    delete: function (k) {
      k = String(k);
      return Promise.resolve({ key: k, deleted: kept.delete(k), shared: false });
    },
    list: function (prefix) {
      var keys = Array.from(kept.keys()).filter(function (k) { return !prefix || k.indexOf(prefix) === 0; });
      return Promise.resolve({ keys: keys, prefix: prefix, shared: false });
    }
  });
  var last = -1;
  function size() {
    var root = document.documentElement;
    if (!root) return;
    var height = Math.ceil(root.getBoundingClientRect().height);
    if (height === last) return;
    last = height;
    send({ type: 'size', height: height });
  }
  window.addEventListener('load', size);
  window.addEventListener('resize', size);
  if (window.ResizeObserver) {
    new ResizeObserver(size).observe(document.documentElement);
  }
  document.addEventListener('DOMContentLoaded', size);
})();`;

/**
 * The policy and the script, as the document's first markup. Nothing the page wrote runs, or
 * can hide the guard (a comment, a script string or a stray "<head" before the real head),
 * because the guard comes before all of it.
 */
export function pageGuard(origin) {
  return (
    `<meta http-equiv="Content-Security-Policy" content="${escapeAttribute(pagePolicy(origin))}">` +
    `<script>${PAGE_SCRIPT}</script>`
  );
}

// One anchored check; nothing searches the page, so the cost is linear in the page.
const DOCTYPE = /^\s*<!doctype[^>]*>/iu;

export function pageDocument({ html, resources, origin }) {
  const source = rewriteResources(html, resources, origin);
  // The parser puts the meta and the script in the implied head and then merges the page's own
  // <html> and <head> (lang and other attributes are kept).
  const doctype = DOCTYPE.exec(source);
  return `<!doctype html>${pageGuard(origin)}${doctype ? source.slice(doctype[0].length) : source}`;
}
